/**
 * Calculate optimal text color (black or white) for a given background color
 * Uses WCAG contrast ratio formula
 */

import { hexToRgb } from './colorBlending';

/**
 * Calculate relative luminance of a color
 * https://www.w3.org/TR/WCAG20/#relativeluminancedef
 */
function getRelativeLuminance(r: number, g: number, b: number): number {
  // Convert to 0-1 range
  const [rs, gs, bs] = [r, g, b].map(val => {
    const v = val / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Calculate contrast ratio between two colors
 * https://www.w3.org/TR/WCAG20/#contrast-ratiodef
 */
function getContrastRatio(lum1: number, lum2: number): number {
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Determine whether black or white text has better contrast
 * on the given background color
 * 
 * @param backgroundColor - Hex color string (e.g., "#FFE119")
 * @returns "#000000" for black or "#FFFFFF" for white
 */
export function getOptimalTextColor(backgroundColor: string): string {
  const rgb = hexToRgb(backgroundColor);
  const bgLuminance = getRelativeLuminance(rgb.r, rgb.g, rgb.b);
  
  const whiteLuminance = 1; // White is max luminance
  const blackLuminance = 0; // Black is min luminance
  
  const contrastWithWhite = getContrastRatio(bgLuminance, whiteLuminance);
  const contrastWithBlack = getContrastRatio(bgLuminance, blackLuminance);
  
  // Return the color with higher contrast
  return contrastWithBlack > contrastWithWhite ? '#000000' : '#FFFFFF';
}



























